/* RESPONSIBLE TEAM: team-fin-voice */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type Store from '@ember-data/store';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import {
  DEFAULT_VOICE_LANGUAGE,
  type FinVoiceVoiceLanguage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type { FinVoiceRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import { get } from 'embercom/lib/ajax';
import { request } from 'embercom/lib/inbox/requests';
import ConversationAttributeDescriptor, {
  type ConversationAttributeDescriptorWireFormat,
} from 'embercom/objects/inbox/conversation-attribute-descriptor';
import IpAllowListValidator from 'embercom/lib/ip-allowlist-validator';

export type OfficeHoursScheduleOption = {
  value: string;
  text: string;
  description: string;
  isDefault: boolean;
};

export interface Schedule {
  id: string;
  name: string;
  labelWithTimezone: string;
  isNew: boolean;
  isDefault: boolean;
}

const PERMISSION_TO_CREATE_AND_EDIT_BOTS = 'can_create_and_edit_bots';
const PERMISSION_TO_ACCESS_PRODUCT_SETTINGS = 'can_access_product_settings';

export default class FinVoiceConfigService extends Service {
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare permissionsService: $TSFixMe;
  @service declare router: $TSFixMe;

  @tracked numberConfigurations: Record<string, FinVoiceNumberConfiguration> = {};
  @tracked officeHoursSchedules: OfficeHoursScheduleOption[] = [];
  @tracked phoneNumbers: Record<string, PhoneNumber> = {};
  @tracked conversationAttributeDescriptors: ConversationAttributeDescriptor[] = [];
  @tracked numberConfigurationsLoaded = false;
  @tracked numberConfigurationsByStepId: Map<string, FinVoiceNumberConfiguration> = new Map();

  @tracked cachedCanAccessFinVoice = false;
  private canAccessFinVoicePromise: Promise<void> | null = null;

  async ensureCanAccessFinVoiceLoaded(): Promise<void> {
    if (!this.canAccessFinVoicePromise) {
      this.canAccessFinVoicePromise = this.loadCanAccessFinVoice();
    }
    return this.canAccessFinVoicePromise;
  }

  private async loadCanAccessFinVoice(): Promise<void> {
    try {
      this.cachedCanAccessFinVoice = await this.canAccessFinVoice();
    } catch {
      this.cachedCanAccessFinVoice = false;
      this.canAccessFinVoicePromise = null;
    }
  }

  get defaultHandoverConfiguration(): FinVoiceNumberConfigurationHandoverConfiguration {
    return {
      in: {
        type: 'handover-to-voicemail',
        config: {},
      },
      nin: {
        type: 'handover-to-voicemail',
        config: {},
      },
    };
  }

  get isTrialExperience() {
    return this.appService.app.canUsePhoneFinVoiceFree;
  }

  get shouldShowPaywall(): boolean {
    return !this.appService.app.canSetupFinVoicePhoneEasySetup;
  }

  async canAccessFinVoice(): Promise<boolean> {
    let canSetupFinVoicePhoneEasySetup = this.appService.app.canSetupFinVoicePhoneEasySetup;
    let isSalesOwned = this.appService.app.isSalesforceContracted;

    let hasExistingNumberConfigurations = false;

    // For self-serve customers, check if they have existing number configurations since that means they used it before.
    if (!isSalesOwned) {
      try {
        hasExistingNumberConfigurations = (
          await get(`/ember/inbox/calling_settings`, {
            app_id: this.appService.app.id,
          })
        )?.has_fin_voice_number_configurations;
      } catch (e: unknown) {
        console.error('Failed to fetch number configurations', e);
      }
    }

    return canSetupFinVoicePhoneEasySetup || isSalesOwned || hasExistingNumberConfigurations;
  }

  constructor(owner: object) {
    super(owner);
  }

  getDefaultOfficeHoursSchedule() {
    return this.officeHoursSchedules.find((schedule) => schedule.isDefault)?.value;
  }

  async checkPermissionToCreateAndEditBots() {
    await this.permissionsService.checkPermission(
      PERMISSION_TO_CREATE_AND_EDIT_BOTS,
      this.intl.t('operator.fin.setup.setup-and-go-live.not-authorized'),
    );
  }

  async checkPermissionToAccessProductSettings(enforcePermission = true) {
    if (enforcePermission) {
      try {
        await this.permissionsService.checkPermission(
          PERMISSION_TO_ACCESS_PRODUCT_SETTINGS,
          this.intl.t('operator.fin.setup.setup-and-go-live.not-authorized'),
        );
      } catch (error) {
        // Navigate away when permission check fails
        this.router.transitionTo({ queryParams: { tab: 'voice-playground' } });
        throw error;
      }
    } else {
      return this.permissionsService.currentAdminCan(PERMISSION_TO_ACCESS_PRODUCT_SETTINGS);
    }
  }

  _formatNumberConfigurations(
    numberConfigurations: FinVoiceNumberConfiguration[],
    phoneNumbers: PhoneNumber[],
  ) {
    return phoneNumbers.reduce(
      (acc, phoneNumber) => {
        let numberConfiguration = numberConfigurations.find(
          (config) => config.workspacePhoneNumberId === phoneNumber.id,
        );

        if (numberConfiguration) {
          acc[phoneNumber.id] = numberConfiguration;
        } else if (phoneNumber.inboundEnabled) {
          acc[phoneNumber.id] = this.addNewNumberConfiguration(phoneNumber.id, false);
        }

        return acc;
      },
      {} as Record<string, FinVoiceNumberConfiguration>,
    );
  }

  async loadAllData() {
    await this.checkPermissionToAccessProductSettings();

    if (this.numberConfigurationsLoaded) {
      return;
    }

    let [
      officeHoursSchedules,
      phoneNumbers,
      numberConfigurations,
      conversationAttributeDescriptors,
    ] = await Promise.all([
      this.fetchOfficeHoursSchedules(),
      this.fetchPhoneNumbers(),
      this.fetchConfigurations(),
      this.fetchConversationAttributeDescriptors(),
    ]);

    this.officeHoursSchedules = officeHoursSchedules;
    this.conversationAttributeDescriptors = conversationAttributeDescriptors;
    this.phoneNumbers = phoneNumbers.reduce(
      (acc, phoneNumber) => {
        acc[phoneNumber.id] = phoneNumber;
        return acc;
      },
      {} as Record<string, PhoneNumber>,
    );

    this.numberConfigurations = this._formatNumberConfigurations(
      numberConfigurations,
      phoneNumbers,
    );

    this.numberConfigurationsLoaded = true;
  }

  async fetchConfigurations() {
    return (await this.store.findAll('fin-voice/number-configuration')).toArray();
  }

  getNumberConfigurationForStep(stepId: string): FinVoiceNumberConfiguration | undefined {
    return this.numberConfigurationsByStepId.get(stepId);
  }

  async loadFinVoiceNumberConfigurationForStep(
    stepId: string,
    finVoiceNumberConfigurationId: string,
  ) {
    await this.checkPermissionToAccessProductSettings();

    let numberConfiguration = await this.store.findRecord(
      'fin-voice/number-configuration',
      finVoiceNumberConfigurationId,
    );

    this.numberConfigurationsByStepId.set(stepId, numberConfiguration);
  }

  async createFinVoiceNumberConfigurationForStep(
    stepId: string,
    workspacePhoneNumberType: 'ivr' | 'sip' | 'phone' = 'ivr',
  ): Promise<string> {
    await this.checkPermissionToCreateAndEditBots();
    let configAttributes = this.buildNumberConfigurationAttributesForStep(workspacePhoneNumberType);

    let numberConfiguration = this.store.createRecord(
      'fin-voice/number-configuration',
      configAttributes,
    );

    try {
      await numberConfiguration.save();
      this.numberConfigurationsByStepId.set(stepId, numberConfiguration);
      return numberConfiguration.id;
    } catch (error) {
      // Rollback the record if save fails
      numberConfiguration.rollbackAttributes();
      this.numberConfigurationsByStepId.delete(stepId);
      throw error;
    }
  }

  private buildNumberConfigurationAttributesForStep(
    workspacePhoneNumberType: 'ivr' | 'sip' | 'phone',
  ) {
    return {
      appId: this.appService.app.id,
      predicates: {},
      handoverConfiguration: this.defaultHandoverConfigurationForStep(),
      status: 'active',
      workspacePhoneNumberType,
    };
  }

  private defaultHandoverConfigurationForStep(): FinVoiceNumberConfigurationHandoverConfiguration {
    return {
      in: {
        type: 'handover-resume-ivr-workflow',
        config: { ivrLocale: DEFAULT_VOICE_LANGUAGE },
      },
      nin: {
        type: 'handover-resume-ivr-workflow',
        config: { ivrLocale: DEFAULT_VOICE_LANGUAGE },
      },
    };
  }

  async fetchOfficeHoursSchedules() {
    let officeHoursSchedules: Schedule[] = (
      await this.store.findAll('office-hours-schedule')
    ).toArray();

    return officeHoursSchedules.map((schedule) => ({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
      isDefault: schedule.isDefault,
    }));
  }

  async deleteNumber(phoneNumberId: string) {
    let foundNumber = this.phoneNumbers[phoneNumberId];

    await foundNumber.destroyRecord();

    delete this.phoneNumbers[phoneNumberId];
    delete this.numberConfigurations[phoneNumberId];

    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-agent.voice-playground.line-settings.delete.success', {
        name: foundNumber?.name || foundNumber?.formattedPhoneNumber,
      }),
    );
  }

  async fetchPhoneNumbers() {
    let phoneNumbers = await this.store.findAll('calling-phone-number', { reload: true });
    let includeMissingBundleNumbers = this.appService.app.canUseFeature(
      'fin-voice-show-missing-bundle-numbers',
    );

    return phoneNumbers
      .toArray()
      .filter((phoneNumber) =>
        includeMissingBundleNumbers ? phoneNumber.isActive : phoneNumber.status === 'active',
      );
  }

  async fetchConversationAttributeDescriptors() {
    let appid = this.appService.app.id;
    let response = await request(
      `/ember/conversation_attributes/descriptors?app_id=${appid}&system_defined=false`,
    );
    let json = (await response.json()) as ConversationAttributeDescriptorWireFormat[];
    return json
      .map(ConversationAttributeDescriptor.deserialize)
      .filter((attribute) => attribute.type === 'list' && !attribute.archived);
  }

  addNewNumberConfiguration(phoneNumberId: string, addToNumberConfigurations = true) {
    let phoneNumber = this.phoneNumbers[phoneNumberId];
    let configAttributes = this.buildNumberConfigurationAttributes(phoneNumber);

    let newNumberConfiguration: FinVoiceNumberConfiguration = this.store.createRecord(
      'fin-voice/number-configuration',
      configAttributes,
    );

    if (addToNumberConfigurations) {
      this.numberConfigurations[phoneNumberId] = newNumberConfiguration;
    }

    return newNumberConfiguration;
  }

  private buildNumberConfigurationAttributes(phoneNumber: PhoneNumber) {
    let isExternalProvider = phoneNumber.isExternalProvider;

    return {
      appId: this.appService.app.id,
      workspacePhoneNumber: phoneNumber.phoneNumber,
      predicates: isExternalProvider
        ? { in: { percentageRollout: 100 }, nin: { percentageRollout: 100 } }
        : {},
      handoverConfiguration: this.defaultHandoverConfiguration,
      status: isExternalProvider ? 'active' : 'draft',
    };
  }

  private _validateIpAddress(ipAddress: string): boolean {
    let validator = new IpAllowListValidator(ipAddress, this.intl);
    return validator.isValid;
  }

  private async _updateIpRange(
    phoneNumberId: string,
    ipAddress: string,
    operation: 'add' | 'remove',
  ): Promise<boolean> {
    // Basic input validation
    if (!ipAddress) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.invalid-ip-address'),
      );
      return false;
    }

    if (!this.phoneNumbers[phoneNumberId]) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.phone-number-not-found'),
      );
      return false;
    }

    // Normalize IP address: add /32 if no CIDR prefix is present
    let normalizedIpAddress = ipAddress;
    if (!ipAddress.includes('/')) {
      normalizedIpAddress = `${ipAddress}/32`;
    }

    // Validate IP address format for add operation
    if (operation === 'add' && !this._validateIpAddress(normalizedIpAddress)) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.invalid-ip-address'),
      );
      return false;
    }

    try {
      let phoneNumber = this.phoneNumbers[phoneNumberId];
      let currentIpRange = [...phoneNumber.ipRangeArray]; // Create proper copy to avoid mutation

      if (operation === 'add') {
        // Normalize existing IPs for comparison
        let normalizedCurrentIpRange = currentIpRange.map((ip: string) =>
          ip.includes('/') ? ip : `${ip}/32`,
        );
        // Check for duplicate IP address (compare normalized versions)
        if (normalizedCurrentIpRange.includes(normalizedIpAddress)) {
          this.notificationsService.notifyError(
            this.intl.t('ai-agent.voice-playground.line-settings.ip-address-already-exists'),
          );
          return false;
        }
        currentIpRange.push(normalizedIpAddress);
      } else {
        // Remove operation: normalize stored IPs for comparison
        // The ipAddress passed in will be normalized (with /32) from the display
        // We need to find and remove the matching stored IP regardless of its format
        currentIpRange = currentIpRange.filter((ip: string) => {
          // Normalize stored IP for comparison
          let normalizedStoredIp = ip.includes('/') ? ip : `${ip}/32`;
          // Remove if normalized versions match
          return normalizedStoredIp !== normalizedIpAddress;
        });
      }

      phoneNumber.ipRange = currentIpRange.join(',');
      await phoneNumber.save();
      return true;
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.error-saving-ip-address'),
      );
      return false;
    }
  }

  async addIpAddress(phoneNumberId: string, ipAddress: string): Promise<boolean> {
    return this._updateIpRange(phoneNumberId, ipAddress, 'add');
  }

  async removeIpAddress(phoneNumberId: string, ipAddress: string): Promise<boolean> {
    return this._updateIpRange(phoneNumberId, ipAddress, 'remove');
  }

  async updatePercentageRollout(
    phoneNumberId: string,
    rollOutPercentageIn: number | undefined,
    rollOutPercentageNin: number | undefined,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.predicates = {
      officeHoursSchedule: numberConfiguration.predicates?.officeHoursSchedule,
      in: {
        percentageRollout: rollOutPercentageIn || 0,
      },
      nin: {
        percentageRollout: rollOutPercentageNin || 0,
      },
    };

    numberConfiguration.status = !rollOutPercentageIn && !rollOutPercentageNin ? 'draft' : 'active';

    await numberConfiguration.save();
  }

  async addAndSaveNewOfficeHoursSchedule(phoneNumberId: string, schedule: Schedule) {
    this.officeHoursSchedules.push({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
      isDefault: schedule.isDefault,
    });

    await this.updateOfficeHoursSchedule(phoneNumberId, schedule.id);
  }

  async updateAskForCsatRating(
    phoneNumberId: string,
    askForCsatRating: boolean,
    csatMessage: string,
    csatTtsLanguage: string,
  ) {
    await this.checkPermissionToCreateAndEditBots();
    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.handoverConfiguration.askForCsatRating = askForCsatRating;
    numberConfiguration.handoverConfiguration.csatTtsLanguage = csatTtsLanguage;
    numberConfiguration.csatMessage = csatMessage;
    await numberConfiguration.save();
  }

  async updateOfficeHoursSchedule(phoneNumberId: string, officeHoursSchedule: string) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.predicates.officeHoursSchedule = officeHoursSchedule;

    await numberConfiguration.save();
  }

  async updateVoiceBehavior(
    phoneNumberId: string,
    voice: FinVoiceVoice,
    greeting: string,
    greetingOutOfOfficeHours?: string,
    language?: FinVoiceVoiceLanguage,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.voice = voice;
    numberConfiguration.greeting = greeting;
    numberConfiguration.greetingOutOfOfficeHours = greetingOutOfOfficeHours;

    // Update the phone number's locale if language is provided
    if (language && this.phoneNumbers[phoneNumberId]) {
      this.phoneNumbers[phoneNumberId].locale = language;
      // Save the phone number to persist the locale change
      await this.phoneNumbers[phoneNumberId].save();
    }
    await numberConfiguration.save();
  }

  async updateRunbook(phoneNumberId: string, runbook: FinVoiceRunbook, procedureId: string) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.runbook = runbook;
    numberConfiguration.handoverConfiguration.procedureId = procedureId;
    await numberConfiguration.save();
  }

  async updateAfterPhoneCallEnds(
    numberConfiguration: FinVoiceNumberConfiguration,
    autoCloseWhenNotEscalated: boolean,
    defaultTeamId?: string,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    numberConfiguration.handoverConfiguration.autoCloseWhenNotEscalated = autoCloseWhenNotEscalated;
    numberConfiguration.handoverConfiguration.defaultTeamId = defaultTeamId;
    await numberConfiguration.save();
  }

  async updateFinIssueCategorization(phoneNumberId: string, attributes?: string[]) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.classificationAttributes = attributes;
    await numberConfiguration.save();
  }

  async updateHandoverConfiguration(
    phoneNumberId: string,
    handoverConfiguration: FinVoiceNumberConfigurationHandoverConfiguration,
  ) {
    await this.checkPermissionToCreateAndEditBots();

    let numberConfiguration = this.numberConfigurations[phoneNumberId];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumberId);
    }

    numberConfiguration.handoverConfiguration = {
      ...numberConfiguration.handoverConfiguration,
      ...handoverConfiguration,
    };
    await numberConfiguration.save();
  }
}

declare module '@ember/service' {
  interface Registry {
    finVoiceConfigService: FinVoiceConfigService;
    'fin-voice-config-service': FinVoiceConfigService;
  }
}
